import React, { useState } from "react";
import { View, Text, TextInput, ScrollView, TouchableOpacity, StyleSheet } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTheme } from "../../context/ThemeContext";
import { SearchPageType } from "./index";

const SearchPage = ({ content = {} }) => {
	const theme = useTheme();
	const [searchQuery, setSearchQuery] = useState("");
	const styles = useStyles();

	// Merge with default content
	const defaultContent = SearchPageType.getDefaultContent();
	const mergedContent = {
		...defaultContent,
		...content,
	};

	const handleSearch = (query) => {
		setSearchQuery(query);
		// Implement search logic here
	};

	const renderCategories = () => (
		<View style={styles.section}>
			<Text style={styles.sectionTitle}>Categories</Text>
			<ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.categoriesContainer}>
				{mergedContent.categories.map((category) => (
					<TouchableOpacity key={category.id} style={styles.categoryCard}>
						<Ionicons name={category.icon} size={24} color={theme.colors.primary} />
						<Text style={styles.categoryName}>{category.name}</Text>
						<Text style={styles.categoryCount}>{category.count}</Text>
					</TouchableOpacity>
				))}
			</ScrollView>
		</View>
	);

	const renderRecentSearches = () => (
		<View style={styles.section}>
			<View style={styles.sectionHeader}>
				<Text style={styles.sectionTitle}>Recent Searches</Text>
				<TouchableOpacity>
					<Text style={styles.clearText}>Clear All</Text>
				</TouchableOpacity>
			</View>
			{mergedContent.recentSearches.map((search) => (
				<TouchableOpacity key={search.id} style={styles.searchItem}>
					<Ionicons name="time-outline" size={20} color={theme.colors.textSecondary} />
					<Text style={styles.searchText}>{search.query}</Text>
					<TouchableOpacity>
						<Ionicons name="close-outline" size={20} color={theme.colors.textSecondary} />
					</TouchableOpacity>
				</TouchableOpacity>
			))}
		</View>
	);

	const renderTrendingSearches = () => (
		<View style={styles.section}>
			<Text style={styles.sectionTitle}>Trending Searches</Text>
			{mergedContent.trendingSearches.map((search, index) => (
				<TouchableOpacity key={search.id} style={styles.trendingItem}>
					<Text style={styles.trendingNumber}>{index + 1}</Text>
					<Text style={styles.searchText}>{search.query}</Text>
					<Text style={styles.trendingCount}>{search.count} searches</Text>
				</TouchableOpacity>
			))}
		</View>
	);

	return (
		<View style={styles.container}>
			<View style={styles.searchContainer}>
				<Ionicons name="search-outline" size={20} color={theme.colors.textSecondary} />
				<TextInput style={styles.searchInput} placeholder="Search books, discussions, events..." placeholderTextColor={theme.colors.textSecondary} value={searchQuery} onChangeText={handleSearch} />
				{searchQuery ? (
					<TouchableOpacity onPress={() => handleSearch("")}>
						<Ionicons name="close-circle" size={20} color={theme.colors.textSecondary} />
					</TouchableOpacity>
				) : null}
			</View>

			<ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
				{!searchQuery ? (
					<>
						{renderCategories()}
						{renderRecentSearches()}
						{renderTrendingSearches()}
					</>
				) : (
					<View style={styles.searchResults}>{/* Implement search results here */}</View>
				)}
			</ScrollView>
		</View>
	);
};

const useStyles = () => {
	const theme = useTheme();

	return StyleSheet.create({
		container: {
			flex: 1,
			backgroundColor: theme.colors.systemBackground.secondary,
		},
		searchContainer: {
			flexDirection: "row",
			alignItems: "center",
			backgroundColor: theme.colors.systemBackground.primary,
			margin: theme.spacing.md,
			padding: theme.spacing.sm,
			borderRadius: theme.borderRadius.lg,
			gap: theme.spacing.sm,
			...theme.shadows.sm,
		},
		searchInput: {
			flex: 1,
			...theme.typography.body,
			color: theme.colors.text,
		},
		content: {
			flex: 1,
		},
		section: {
			marginBottom: theme.spacing.lg,
		},
		sectionHeader: {
			flexDirection: "row",
			justifyContent: "space-between",
			alignItems: "center",
			paddingHorizontal: theme.spacing.md,
			marginBottom: theme.spacing.sm,
		},
		sectionTitle: {
			...theme.typography.title3,
			color: theme.colors.text,
			marginLeft: theme.spacing.md,
			marginBottom: theme.spacing.sm,
		},
		clearText: {
			...theme.typography.subhead,
			color: theme.colors.primary,
		},
		categoriesContainer: {
			paddingHorizontal: theme.spacing.md,
		},
		categoryCard: {
			backgroundColor: theme.colors.systemBackground.primary,
			borderRadius: theme.borderRadius.lg,
			padding: theme.spacing.md,
			marginRight: theme.spacing.md,
			alignItems: "center",
			minWidth: 100,
			...theme.shadows.sm,
		},
		categoryName: {
			...theme.typography.subhead,
			color: theme.colors.text,
			marginTop: theme.spacing.sm,
		},
		categoryCount: {
			...theme.typography.caption1,
			color: theme.colors.textSecondary,
			marginTop: theme.spacing.xxs,
		},
		searchItem: {
			flexDirection: "row",
			alignItems: "center",
			paddingVertical: theme.spacing.sm,
			paddingHorizontal: theme.spacing.md,
			gap: theme.spacing.sm,
		},
		trendingItem: {
			flexDirection: "row",
			alignItems: "center",
			paddingVertical: theme.spacing.sm,
			paddingHorizontal: theme.spacing.md,
			gap: theme.spacing.md,
		},
		searchText: {
			flex: 1,
			...theme.typography.body,
			color: theme.colors.text,
		},
		trendingNumber: {
			...theme.typography.title3,
			color: theme.colors.primary,
			width: 24,
			textAlign: "center",
		},
		trendingCount: {
			...theme.typography.caption1,
			color: theme.colors.textSecondary,
		},
		searchResults: {
			flex: 1,
			padding: theme.spacing.md,
		},
	});
};

export default SearchPage;
